import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import ProjectCard from "./ProjectCards";
import ProjectList from "./ProjectList";
import Particle from "../Particle";
import NLP from "../../Assets/Papers/NLP.pdf";

const importAll = (r) =>
  r.keys().reduce((acc, item) => {
    acc[item.replace("./", "")] = r(item);
    return acc;
  }, {});

const images = importAll(
  require.context("../../Assets/Projects", false, /\.(png|jpe?g|svg|webp)$/)
);

const chunkProjects = (items, size) =>
  items.reduce((chunks, item, index) => {
    if (index % size === 0) {
      chunks.push(items.slice(index, index + size));
    }
    return chunks;
  }, []);

const featuredProjects = [
  {
    imgPath: images["logstore.png"], 
    title: "Distributed Log Storage Engine",
    description:
      "A Kafka-inspired log storage system built with leader-based partitioning, append-only writes, crash recovery, tablet-based sharding, and leader-follower replication.",
    ghLink: "https://github.com/sauravjaiswalsj/LogStore",
    demoLink: "https://logstore-ui.onrender.com/",
  },
  {
    imgPath: images["leetcode.png"],
    title: "DSA Tracker Chrome Extension",
    description:
      "A Chrome extension for tracking daily DSA practice, logging solved problems, and staying consistent over time.",
    ghLink: "https://github.com/sauravjaiswalsj/DSA-Tracker-Extension",
  },
  {
    imgPath: images["snake.png"],
    title: "Snake AI Game",
    description:
      "A snake game with AI-powered snake that learns to play the game by itself.",
    ghLink: "https://github.com/sauravjaiswalsj/snake-ais",
  },
  {
    imgPath: "",
    title: "DocumentReader RAG System",
    description:
      "A Spring AI and LangChain based RAG system for financial documents with semantic search, entity extraction, vector-backed retrieval, and a React frontend for querying.",
  },
  // {
  //   imgPath: "",
  //   title: "Bitcoin Price Analysis Platform",
  //   description:
  //     "A Python-based market analysis project that processes 2,000+ days of Bitcoin data, applies moving-average trading signals, and predicts next-day trends with regression.",
  // },
  {
    title: "NLP Sentiment Analysis System",
    description:
      "A sentiment analysis pipeline using transformers and classical NLP preprocessing to classify 50,000+ text samples with strong F1 performance on review data.",
      demoLink: NLP,
  },
  {
    title: "NotesJ",
    description:
      "A notes application and personal blog designed for writing, organizing, and publishing ideas in a clean, focused interface.",
    ghLink: "https://notesj.vercel.app/",
    demoLink: "https://notesj.vercel.app/",
  },
  {
    title: "Expensify.ai",
    description:
      "A full-stack expense tracker built to showcase secure backend engineering with Spring Boot, MongoDB, JWT auth, RBAC, and AI-ready modules.",
    ghLink: "https://github.com/sauravjaiswalsj/Expensify",
  },
  {
    imgPath: images["quillpad.png"],
    title: "QuillPad",
    description:
      "A desktop notepad application with file management and editing operations like cut, copy, paste, undo, and redo. Built with C++ and Qt.",
    ghLink: "https://github.com/sauravjaiswalsj/Notepad-QT",
  },
  
  {
    imgPath: images["auth.jpeg"],
    title: "Authentication System Design",
    description:
      "An Express.js auth system with registration, login, encrypted password storage, and protected user flows.",
    ghLink: "https://github.com/sauravjaiswalsj/MernAuth",
  },
];

const projects = [
  { title: "BookRepos", link: "https://github.com/sauravjaiswalsj/BookRepos" },
  { title: "Github Finder", link: "https://github.com/sauravjaiswalsj/Github-Finder" },
  { title: "FilmFever", link: "https://github.com/sauravjaiswalsj/FilmFever/" },
  { title: "DotMoDe, Assistive Tech", link: "https://www.youtube.com/watch?v=JagqE128LQM" },
  { title: "ClimateDaily", link: "https://climatedaily.vercel.app/" },
  { title: "Portfolio", link: "https://sauravjaiswalsj.github.io/" },
  { title: "Search Crawler", link: "https://gitlab.com/sauravjaiswalsj/searchcrawler#" },
  { title: "BOT Search", link: "https://github.com/sauravjaiswalsj/backend-Projects/tree/main/WhatsAppSearchBot" },
  { title: "Library System Design", link: "https://github.com/sauravjaiswalsj/backend-Projects/tree/main/LibraryManagement" },
  { title: "URL Shortner", link: "https://github.com/sauravjaiswalsj/MicroUrl" },
  { title: "FyndCare", link: "https://www.linkedin.com/company/fyndcare/" },
  { title: "Signature Encryption", link: "https://github.com/sauravjaiswalsj/paper" },
  { title: "Random Thoughts", link: "https://thoughtmix.vercel.app/" },
  { title: "GoodJS", link: "https://github.com/sauravjaiswalsj/Everyday-Javascript" },
  { title: "Movie Seating App", link: "https://sitemovie.netlify.app" },
  { title: "Todo list", link: "https://sauravjaiswalsj.github.io/todo-list/" },
  { title: "Background Color Flipper", link: "https://backc.netlify.app" },
  { title: "Web Drum", link: "https://webdrum.netlify.app" },
];

function Projects() {
  const [showMoreProjects, setShowMoreProjects] = useState(false);
  const projectGroups = chunkProjects(projects, 7);

  return (
    <Container fluid className="project-section">
      <Particle />
      <Container>
        <div className="project-hero">
          <p className="project-kicker">Selected Work</p>
          <h1 className="project-heading">
            Distributed systems, applied AI, and backend-heavy product work.
          </h1>
          <p className="project-subheading">
            Updated from my recent resume to better reflect the systems, RAG,
            and full-stack projects that match my current engineering profile.
          </p>
        </div>
        <Row className="project-grid-row" style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {featuredProjects.map((project) => (
            <Col md={4} className="project-card" key={project.title}>
              <ProjectCard
                imgPath={project.imgPath}
                isBlog={false}
                title={project.title}
                description={project.description}
                ghLink={project.ghLink}
                demoLink={project.demoLink}
              />
            </Col>
          ))}
        </Row>
        <div className="project-more-shell">
          <Button
            variant="primary"
            className="project-more-button"
            type="button"
            onClick={() => setShowMoreProjects((value) => !value)}
          >
            {showMoreProjects ? "Hide More Projects" : "View More Projects"}
          </Button>
        </div>
        {showMoreProjects && (
          <Row className="justify-content-center">
            {projectGroups.map((group, index) => (
              <Col md={6} className="project-card" key={`group-${index}`}>
                <ProjectList
                  title={index === 0 ? "More Projects" : "More Projects II"}
                  projects={group}
                />
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </Container>
  );
}

export default Projects;
